import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import {
  CheckCircle2,
  Clock,
  Droplets,
  Filter,
  Info,
  MapPin,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { FuelAnomalyEventDto, ListFuelAnomalyParams } from '@repo/api'

import PageHeader from '@/components/layout/page-header'
import BetaBadge from '@/components/shared/beta-badge'
import { ListPagination } from '@/components/shared/list-pagination'
import { useAcknowledgeFuelAnomaly } from '@/features/fuel-anomaly/hooks/use-acknowledge-fuel-anomaly'
import { useFuelAnomalyEvents } from '@/features/fuel-anomaly/hooks/use-fuel-anomaly-events'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'

export const Route = createFileRoute('/dashboard/fuel-anomaly/')({
  head: () => ({ meta: [{ title: localizedPageTitle('fuelAnomaly') }] }),
  component: FuelAnomalyPage,
})

/** Theft magnitude: euro when tank capacity is known, else the raw % drop. */
function formatMagnitude(item: FuelAnomalyEventDto): string {
  if (item.dropLiters != null) {
    const litersLabel = `${Math.round(item.dropLiters)} L`
    return item.estimatedEur != null
      ? `${litersLabel} · €${item.estimatedEur.toFixed(2)}`
      : litersLabel
  }
  return `~${Math.round(item.dropPercent)}%`
}

function FuelAnomalyPage() {
  useDocumentTitle('fuelAnomaly')
  const { t } = useTranslation('fuelAnomaly')
  const [filters, setFilters] = useState<ListFuelAnomalyParams>({
    limit: 10,
    offset: 0,
    acknowledged: false,
  })

  const { data, isLoading } = useFuelAnomalyEvents(filters)
  const acknowledge = useAcknowledgeFuelAnomaly()

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        hideBackButton
        titleBadge={<BetaBadge />}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
          <Filter className="h-4 w-4" />
          {t('filters')}
        </div>
        <Select
          value={
            filters.acknowledged == null
              ? 'all'
              : filters.acknowledged
                ? 'true'
                : 'false'
          }
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              acknowledged: v === 'all' ? undefined : v === 'true',
              offset: 0,
            }))
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder={t('allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allStatuses')}</SelectItem>
            <SelectItem value="false">{t('openOnly')}</SelectItem>
            <SelectItem value="true">{t('dismissedOnly')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border">
        <div className="min-w-[780px]">
          <div className="text-muted-foreground grid grid-cols-[1fr_1fr_7rem_5.5rem_7rem_6rem] gap-4 border-b px-4 py-3 text-xs font-medium">
            <span>{t('vehicleColumn')}</span>
            <span>{t('typeColumn')}</span>
            <span>{t('amountColumn')}</span>
            <span>{t('locationColumn')}</span>
            <span>{t('whenColumn')}</span>
            <span>{t('statusColumn')}</span>
          </div>

          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="border-b px-4 py-3">
                <Skeleton className="h-5 w-full" />
              </div>
            ))}

          {!isLoading && data?.items.length === 0 && (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-12 text-center text-sm">
              <CheckCircle2 className="h-10 w-10 opacity-30" />
              <p>{t('noEvents')}</p>
            </div>
          )}

          {data?.items.map((item) => (
            <div
              key={item.id}
              className="grid grid-cols-[1fr_1fr_7rem_5.5rem_7rem_6rem] items-center gap-4 border-b px-4 py-3 text-sm last:border-b-0"
            >
              <span className="font-medium">{item.licensePlate ?? '—'}</span>
              <div className="flex items-center gap-1.5">
                <Badge
                  variant="outline"
                  className="text-destructive bg-destructive/10 text-[10px]"
                >
                  <Droplets className="mr-1 h-3 w-3" />
                  {t('type.parked_siphon')}
                </Badge>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Info className="text-muted-foreground h-3 w-3 shrink-0 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-56 text-wrap">
                    {t('typeTooltip.parked_siphon')}
                  </TooltipContent>
                </Tooltip>
              </div>
              <span className="font-medium tabular-nums">
                {formatMagnitude(item)}
              </span>
              {item.latitude != null && item.longitude != null ? (
                <a
                  href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary flex items-center gap-1 text-xs hover:underline"
                >
                  <MapPin className="h-3 w-3 shrink-0" />
                  {t('viewMap')}
                </a>
              ) : (
                <span className="text-muted-foreground text-xs">—</span>
              )}
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <span className="text-muted-foreground flex items-center gap-1 text-xs">
                    <Clock className="h-3 w-3" />
                    {format(new Date(item.occurredAt), 'dd/MM HH:mm')}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t('windowRange', {
                    from: format(new Date(item.windowStart), 'dd/MM/yyyy HH:mm'),
                    to: format(new Date(item.windowEnd), 'dd/MM/yyyy HH:mm'),
                  })}
                </TooltipContent>
              </Tooltip>
              <div>
                {item.acknowledged ? (
                  <Badge
                    variant="outline"
                    className="bg-green-500/10 text-[10px] text-green-600 dark:text-green-400"
                  >
                    <CheckCircle2 className="mr-0.5 h-3 w-3" />
                    {t('dismissedBadge')}
                  </Badge>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={acknowledge.isPending}
                    onClick={() => acknowledge.mutate({ id: item.id })}
                  >
                    {t('dismiss')}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data &&
        (() => {
          const pageSize = filters.limit ?? 10
          const offset = filters.offset ?? 0
          const page = Math.floor(offset / pageSize) + 1
          const totalPages = Math.max(1, Math.ceil(data.total / pageSize))
          return (
            <ListPagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={data.total}
              hasPrevious={page > 1}
              hasNext={page < totalPages}
              onPageChange={(p) =>
                setFilters((f) => ({
                  ...f,
                  offset: (p - 1) * (f.limit ?? 10),
                }))
              }
              onPageSizeChange={(size) =>
                setFilters((f) => ({ ...f, limit: size, offset: 0 }))
              }
              className="px-4"
            />
          )
        })()}
    </div>
  )
}
